import { Component } from "@theme/component";
import { isClickedOutside } from "@theme/utilities";

/**
 * Share links are built from the URL already in the address bar, so everything that
 * makes a page specific — the selected variant, a collection's filters and sort, a
 * search term, the page number, the locale — travels automatically, whatever scheme
 * the page happens to use to express it. Nothing here needs to know about any of it.
 *
 * What is removed is only sign-in and session state, which is the one category that is
 * actively harmful in a link handed to someone else rather than merely untidy. Shop's
 * sign-in flow is the reported case and leaves a whole `shop_` family behind, so that
 * is matched by prefix and future members are covered without touching this list.
 *
 * Deliberately NOT removed: campaign and ad-click params (utm_*, gclid, fbclid). They
 * make a shared link attribute to the sharer's original click, which is worth fixing,
 * but it is an analytics decision rather than part of this behaviour, and guessing at
 * params we have not seen risks dropping something a recipient needed.
 */
const SHARE_DENY_PREFIX = "shop_";

const SHARE_DENY_PARAMS = [
  // Sign-in, customer-account and multipass state.
  "_st",
  "login_token",
  "return_to",
  "checkout_url",
  "customer_account_redirect",
  "id_token",
  "code",
  "state",
  "hmac",
  "timestamp",
  // Section Rendering API params. They never reach the address bar on their own, but on
  // a page URL they make the response a bare HTML fragment, so a link carrying one is
  // broken for whoever opens it.
  "section_id",
  "resources[type]",
  // Re-added below from the viewer's own context rather than reflected from the URL.
  "credit_user",
];

/**
 * Drops the params that must not be shared, in place. Keys are snapshotted before
 * deleting, since mutating searchParams while iterating it skips entries.
 * @param {URL} url
 */
function stripUnsharedParams(url) {
  for (const param of [...url.searchParams.keys()]) {
    if (param.startsWith(SHARE_DENY_PREFIX) || SHARE_DENY_PARAMS.includes(param)) {
      url.searchParams.delete(param);
    }
  }
}

/**
 * A credit id is C (customer) or R (UnFranchise repID) followed by digits, mirroring the
 * validation in portal-manager.js. Applied to the credit id a visitor arrived with, so a
 * share link can never reflect an arbitrary value back out.
 */
const CREDIT_USER_PATTERN = /^[CR]\d{1,15}$/;

/**
 * A portal id is alphanumeric with hyphens and underscores, mirroring the validation
 * in portal-manager.js. A cookie value that fails it is treated as absent rather than
 * spliced into a URL path.
 */
const PORTAL_ID_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/;

/**
 * Carries the portal as the first path segment rather than a query param, which is the
 * form the storefront's edge redirect expects: /{portal}/{path} comes back as
 * {path}?portal_id={portal} with the rest of the query string intact.
 *
 * The portal has to sit BEFORE any locale prefix — /portal/zh/products/x resolves,
 * /zh/portal/products/x does not — which is what happens naturally here, since the
 * pathname being prefixed already carries the locale.
 *
 * @param {URL} url
 * @param {string} portalId
 */
function applyPortalPath(url, portalId) {
  if (!PORTAL_ID_PATTERN.test(portalId)) return;
  // Only this store's own URLs go through the edge redirect.
  if (url.origin !== window.location.origin) return;

  // The redirect re-adds portal_id itself, so a stale one would duplicate the portal.
  url.searchParams.delete("portal_id");

  const prefix = `/${portalId}`;
  if (url.pathname === prefix || url.pathname.startsWith(`${prefix}/`)) return;

  // The homepage is "/", and the edge expects "/portal" rather than "/portal/".
  url.pathname = url.pathname === "/" ? prefix : prefix + url.pathname;
}

/**
 * Social share block: platform share buttons plus a copy-link dialog.
 *
 * @typedef {object} SocialShareRefs
 * @property {HTMLDialogElement} modal - The copy-link dialog.
 * @property {HTMLInputElement} [urlInput] - The share URL input.
 * @property {HTMLButtonElement} [copyButton] - The copy button.
 *
 * @extends {Component<SocialShareRefs>}
 */
class SocialShare extends Component {
  requiredRefs = ["modal"];

  /** @type {string} */
  copiedText = "Copied!";

  /** @type {string} */
  #copyButtonOriginalText = "";

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #copyTimeout;

  /** @type {number} */
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();
    this.copiedText = this.dataset.copiedText || "Copied!";
    this.#copyButtonOriginalText = this.refs.copyButton?.textContent?.trim() || "";
    // Native Escape / backdrop dismissal fires `close`; reset the button label.
    this.refs.modal.addEventListener("close", this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.modal.removeEventListener("close", this.#handleDialogClose);
    // Restore the page if we're torn down while the dialog is still open.
    if (this.refs.modal.open) this.#unlockScroll();
    clearTimeout(this.#copyTimeout);
  }

  /**
   * Build the share URL, keeping only the query params the recipient needs and adding
   * the portal and the credit id.
   * Portal id: tries PortalManager.getPortalId() first; falls back to the
   * portal_id cookie when the manager has not loaded yet or returns a falsy value.
   * Credit id: the viewer's own id when they are signed in, otherwise the one they
   * arrived with, so a referral keeps its attribution when a guest passes the link on.
   * @returns {string}
   */
  getShareUrl() {
    const url = new URL(window.location.href);

    // Read before stripping: the inbound credit id is dropped along with everything else.
    const inboundCreditUser = url.searchParams.get("credit_user") || "";

    stripUnsharedParams(url);

    const portalManager = /** @type {any} */ (window).PortalManager;
    const portalId =
      (portalManager && typeof portalManager.getPortalId === "function" ? portalManager.getPortalId() : null) ||
      this.#getCookie("portal_id");

    const creditUser = this.getCreditUser() || (CREDIT_USER_PATTERN.test(inboundCreditUser) ? inboundCreditUser : "");

    if (portalId) {
      applyPortalPath(url, portalId);
    }
    if (creditUser) {
      url.searchParams.set("credit_user", creditUser);
    }

    return url.toString();
  }

  /**
   * Read the logged-in customer's referral credit id from the `ma:customer` meta tag
   * (rendered by snippets/meta-tags.liquid). Empty for anonymous customers, so their
   * share links carry no credit_user.
   * @returns {string}
   */
  getCreditUser() {
    const meta = /** @type {HTMLMetaElement | null} */ (document.querySelector('meta[name="ma:customer"]'));
    return meta?.dataset.creditUser || "";
  }

  /**
   * Open a centered popup window.
   * @param {string} url
   * @param {number} [width]
   * @param {number} [height]
   */
  openPopup(url, width = 600, height = 400) {
    const left = (screen.width - width) / 2;
    const top = (screen.height - height) / 2;
    window.open(url, "share", `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`);
  }

  /**
   * Handle email share. Subject and body come from the block settings; the product
   * share URL is appended after the body text.
   */
  handleEmail() {
    const shareUrl = this.getShareUrl();
    const subject = this.dataset.emailSubject || document.title;
    const bodyText = this.dataset.emailBody || "";
    const body = bodyText ? `${bodyText} ${shareUrl}` : shareUrl;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  /**
   * Handle Facebook share.
   */
  handleFacebook() {
    const shareUrl = this.getShareUrl();
    this.openPopup(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`);
  }

  /**
   * Handle X (Twitter) share.
   */
  handleX() {
    const shareUrl = this.getShareUrl();
    this.openPopup(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(document.title)}`
    );
  }

  /**
   * Handle Pinterest share.
   */
  handlePinterest() {
    const shareUrl = this.getShareUrl();
    const ogImage = /** @type {HTMLMetaElement | null} */ (document.querySelector('meta[property="og:image"]'));
    const image = ogImage?.content || "";
    this.openPopup(
      `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&media=${encodeURIComponent(image)}&description=${encodeURIComponent(document.title)}`
    );
  }

  /**
   * Handle TikTok share — opens the copy-link dialog.
   */
  handleTiktok() {
    this.showModal();
  }

  /**
   * Handle Instagram share — opens the copy-link dialog.
   */
  handleInstagram() {
    this.showModal();
  }

  /**
   * Handle copy link — opens the copy-link dialog.
   */
  handleCopyLink() {
    this.showModal();
  }

  /**
   * Show the copy-link dialog and populate the URL input.
   */
  showModal() {
    const { modal, urlInput } = this.refs;
    if (modal.open) return;
    if (urlInput) {
      urlInput.value = this.getShareUrl();
    }
    this.#lockScroll();
    modal.showModal();
  }

  /**
   * Pin the page behind the dialog so the background can't scroll while it's open.
   */
  #lockScroll() {
    this.#previousScrollY = window.scrollY;
    document.body.style.width = "100%";
    document.body.style.position = "fixed";
    document.body.style.top = `-${this.#previousScrollY}px`;
  }

  /**
   * Release the scroll lock and restore the previous scroll position.
   */
  #unlockScroll() {
    document.body.style.width = "";
    document.body.style.position = "";
    document.body.style.top = "";
    window.scrollTo({ top: this.#previousScrollY, behavior: "instant" });
  }

  /**
   * Hide the copy-link dialog.
   */
  hideModal() {
    this.refs.modal.close();
  }

  /**
   * Handle close button click.
   */
  handleCloseModal() {
    this.hideModal();
  }

  /**
   * Close when the click lands outside the dialog content (on the backdrop).
   * Uses geometry rather than event.target so clicks inside the content — including
   * its padding — never close the dialog.
   * @param {MouseEvent} event
   */
  handleBackdropClick(event) {
    if (isClickedOutside(event, this.refs.modal)) {
      this.hideModal();
    }
  }

  /**
   * Copy the share URL to the clipboard and show feedback.
   */
  async handleCopy() {
    const { urlInput } = this.refs;
    if (!urlInput) return;

    try {
      await navigator.clipboard.writeText(urlInput.value);
      this.showCopiedFeedback();
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — select the text so
      // the user can copy it manually.
      urlInput.select();
    }
  }

  /**
   * Briefly change the copy button text to the "copied" label.
   */
  showCopiedFeedback() {
    const { copyButton } = this.refs;
    if (!copyButton) return;
    copyButton.textContent = this.copiedText;
    clearTimeout(this.#copyTimeout);
    this.#copyTimeout = setTimeout(() => {
      this.resetCopyButton();
    }, 2000);
  }

  /**
   * Reset the copy button to its original label.
   */
  resetCopyButton() {
    const { copyButton } = this.refs;
    if (!copyButton || !this.#copyButtonOriginalText) return;
    copyButton.textContent = this.#copyButtonOriginalText;
  }

  /**
   * Read a cookie value by name. Used to resolve portal_id when PortalManager
   * has not initialised yet (mirrors the helper in copy-to-clipboard-footer.js).
   * @param {string} name
   * @returns {string}
   */
  #getCookie(name) {
    const match = document.cookie.match(new RegExp("(?:^|;\\s*)" + name + "=([^;]*)"));
    const value = match?.[1];
    return value === undefined ? "" : decodeURIComponent(value);
  }

  #handleDialogClose = () => {
    this.#unlockScroll();
    this.resetCopyButton();
  };
}

customElements.define("social-share", SocialShare);
